import React from 'react';
import { Box, Card, CardContent, Typography, alpha } from '@mui/material';
import { TrendingUp, TrendingDown } from '@mui/icons-material';

const RiskGauge = ({ 
  title, 
  value, 
  max = 100, 
  thresholds = [30, 50, 70], 
  colors = ['#10b981', '#f59e0b', '#ef4444', '#991b1b'],
  subtitle,
  inverted = false,
}) => {
  // Ensure colors array has all values
  const safeColors = [
    colors[0] || '#10b981',
    colors[1] || '#f59e0b',
    colors[2] || '#ef4444',
    colors[3] || '#991b1b',
  ];

  // Determine color based on value and thresholds
  const getColor = () => {
    const normalizedValue = inverted ? max - value : value;
    if (normalizedValue < thresholds[0]) return safeColors[0];
    if (normalizedValue < thresholds[1]) return safeColors[1];
    if (normalizedValue < thresholds[2]) return safeColors[2];
    return safeColors[3];
  };

  const color = getColor();
  const percentage = (value / max) * 100;
  const trend = value > 50 ? 'up' : 'down';

  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        borderRadius: 2.5,
        border: '1px solid',
        borderColor: 'divider',
        background: '#ffffff',
        position: 'relative',
        overflow: 'hidden',
        transition: 'all 0.2s ease',
        '&:hover': {
          borderColor: alpha(color, 0.3),
          boxShadow: `0 4px 12px ${alpha(color, 0.1)}`,
        },
      }}
    >
      <CardContent sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header with Status */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
          <Box sx={{ flex: 1 }}>
            <Typography 
              variant="overline" 
              sx={{ 
                fontSize: '0.6875rem',
                fontWeight: 700,
                letterSpacing: 0.5,
                color: 'text.secondary',
                lineHeight: 1,
                display: 'block',
                mb: 0.5,
              }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.625rem', lineHeight: 1.3 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              width: 20,
              height: 20,
              borderRadius: 0.75,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(color, 0.12),
              color: color,
            }}
          >
            {trend === 'up' ? <TrendingUp sx={{ fontSize: 12 }} /> : <TrendingDown sx={{ fontSize: 12 }} />}
          </Box>
        </Box>

        {/* Value Display - Prominent */}
        <Box sx={{ mb: 'auto' }}>
          <Typography 
            variant="h2" 
            sx={{ 
              fontWeight: 700, 
              color: color,
              fontSize: '2.5rem',
              lineHeight: 1,
              fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
              letterSpacing: '-0.02em',
            }}
          >
            {value.toFixed(1)}
            <Typography 
              component="span" 
              sx={{ 
                color: alpha(color, 0.6), 
                ml: 0.5, 
                fontSize: '1.25rem',
                fontWeight: 600,
              }}
            >
              %
            </Typography>
          </Typography>
        </Box>

        {/* Progress Bar - Minimalist */}
        <Box sx={{ mt: 1.5 }}>
          <Box
            sx={{
              height: 3,
              borderRadius: 1.5,
              bgcolor: alpha('#000', 0.06),
              overflow: 'hidden',
              position: 'relative',
              mb: 0.75,
            }}
          >
            {/* Background gradient zones */}
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                right: 0,
                background: `linear-gradient(90deg, 
                  ${alpha(safeColors[0], 0.2)} 0%, 
                  ${alpha(safeColors[0], 0.2)} ${thresholds[0]}%, 
                  ${alpha(safeColors[1], 0.2)} ${thresholds[0]}%, 
                  ${alpha(safeColors[1], 0.2)} ${thresholds[1]}%, 
                  ${alpha(safeColors[2], 0.2)} ${thresholds[1]}%, 
                  ${alpha(safeColors[2], 0.2)} ${thresholds[2]}%, 
                  ${alpha(safeColors[3], 0.2)} ${thresholds[2]}%, 
                  ${alpha(safeColors[3], 0.2)} 100%)`,
              }}
            />
            {/* Current value indicator */}
            <Box
              sx={{
                position: 'absolute',
                left: `${percentage}%`,
                top: -2,
                width: 2,
                height: 7,
                bgcolor: color,
                borderRadius: 1,
                transform: 'translateX(-50%)',
                transition: 'left 0.5s ease',
                boxShadow: `0 0 4px ${alpha(color, 0.4)}`,
              }}
            />
          </Box>
          
          {/* Scale Labels - Minimalist */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 0.25 }}>
            <Typography variant="caption" sx={{ fontSize: '0.625rem', color: 'text.disabled', fontWeight: 500 }}>
              0
            </Typography>
            <Typography variant="caption" sx={{ fontSize: '0.625rem', color: 'text.disabled', fontWeight: 500 }}>
              {thresholds[1]}
            </Typography>
            <Typography variant="caption" sx={{ fontSize: '0.625rem', color: 'text.disabled', fontWeight: 500 }}>
              {max}
            </Typography>
          </Box>
        </Box>
      </CardContent>

      {/* Subtle decorative element */}
      <Box
        sx={{
          position: 'absolute',
          top: -20,
          right: -20,
          width: 80,
          height: 80,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha(color, 0.08)} 0%, transparent 70%)`,
          pointerEvents: 'none',
        }}
      />
    </Card>
  );
};

export default RiskGauge;
